import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  getSpacesListQueryKey,
  spacesCreate,
  useSpacesList,
} from '@/shared/api/generated/spaces/spaces';
import { ApiError } from '@/shared/api/http';
import { unwrap } from '@/shared/api/unwrap';
import { t } from '@/shared/i18n';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Field } from '@/shared/ui/Field';
import { Select, SelectItem } from '@/shared/ui/Select';
import { toast } from '@/shared/ui/Toast';

import { spaceSchema, type SpaceValues } from './schema';
import { SpaceForm } from './SpaceForm';

interface Props {
  workspace: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateSpaceDialog({ open, onOpenChange, workspace }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && <Body workspace={workspace} onOpenChange={onOpenChange} />}
    </Dialog>
  );
}

function Body({ workspace, onOpenChange }: Omit<Props, 'open'>) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [template, setTemplate] = useState('');
  // Проекты-шаблоны организации — заготовки со свойствами, тегами и досками.
  const spaces = useSpacesList(workspace, { query: { select: unwrap } });
  const templates = (spaces.data?.items ?? []).filter((item) => item.is_template);

  const form = useForm<SpaceValues>({
    resolver: zodResolver(spaceSchema),
    defaultValues: { name: '', slug: '', description: '', color: '4b8ef5' },
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      const created = unwrap(
        await spacesCreate(workspace, {
          ...values,
          ...(template === '' ? {} : { template_slug: template }),
        }),
      );

      toast.success(t.space.created);
      await queryClient.invalidateQueries({ queryKey: getSpacesListQueryKey(workspace) });
      onOpenChange(false);
      await navigate({
        to: '/app/w/$slug/s/$space',
        params: { slug: workspace, space: created.slug },
      });
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 409) {
        form.setError('slug', { message: t.space.slugTaken });

        return;
      }

      form.setError('name', {
        message: cause instanceof ApiError ? cause.detail : t.common.unknownError,
      });
    }
  });

  return (
    <DialogContent
      title={t.space.createTitle}
      description={t.space.createText}
      footer={
        <>
          <Button
            variant="outline"
            tone="neutral"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t.common.cancel}
          </Button>
          <Button type="submit" form="create-space" loading={form.formState.isSubmitting}>
            {t.space.create}
          </Button>
        </>
      }
    >
      <SpaceForm
        form={form}
        mode="create"
        id="create-space"
        onSubmit={(event) => {
          // Событие — в handleSubmit: без него форма отправится по-настоящему,
          // страница перезагрузится и оборвёт запрос (на телефоне — до ответа).
          event.preventDefault();
          void submit(event);
        }}
      >
        {templates.length > 0 && (
          <Field
            label={t.templates.space.pick}
            hint={template === '' ? undefined : t.templates.space.hint}
          >
            <Select
              value={template === '' ? 'none' : template}
              onValueChange={(value) => {
                setTemplate(value === 'none' ? '' : value);
              }}
            >
              <SelectItem value="none">{t.templates.space.none}</SelectItem>
              {templates.map((item) => (
                <SelectItem key={item.id} value={item.slug}>
                  {item.name}
                </SelectItem>
              ))}
            </Select>
          </Field>
        )}
      </SpaceForm>
    </DialogContent>
  );
}
